import {ChangeEvent, FC, ReactNode} from 'react';
import {Box, IconButton, InputAdornment, TextField, Tooltip} from '@mui/material';
import {FileDownloadOutlined, SearchOutlined} from '@mui/icons-material';
import {useIntl} from 'react-intl';
import {tableToolbarSx} from './TableToolbar.sx';

interface TableToolbarProps {
    icon: ReactNode;
    title: ReactNode;
    totalCount: number;
    totalLabel?: string;
    searchEnabled: boolean;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onExport: () => void;
    exportDisabled?: boolean;
}

export const TableToolbar: FC<TableToolbarProps> = ({
    icon,
    title,
    totalCount,
    totalLabel,
    searchEnabled,
    searchValue,
    onSearchChange,
    onExport,
    exportDisabled,
}) => {
    const intl = useIntl();

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
        onSearchChange(e.target.value);
    };

    const countLabel = totalLabel ?? intl.formatMessage({id: 'total'}).toLowerCase();

    return (
        <Box sx={tableToolbarSx.titleRow}>
            <Box sx={tableToolbarSx.titleIcon}>{icon}</Box>
            <Box component="span" sx={tableToolbarSx.titleText}>{title}</Box>
            <Box sx={tableToolbarSx.rightSide}>
                <Box component="span" sx={tableToolbarSx.titleCount}>
                    {totalCount.toLocaleString()} {countLabel}
                </Box>
                {searchEnabled && (
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder={intl.formatMessage({id: 'search'})}
                        value={searchValue}
                        onChange={handleSearchChange}
                        sx={tableToolbarSx.searchInput}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined sx={tableToolbarSx.searchAdornment}/>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                <Tooltip title={intl.formatMessage({id: 'aiAgentTableExportCsv'})}>
                    <span>
                        <IconButton
                            size="small"
                            sx={tableToolbarSx.iconButton}
                            onClick={onExport}
                            disabled={exportDisabled}
                        >
                            <FileDownloadOutlined/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};
